import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { DialogActions, DialogContent, DialogTitle, Grid, Stack } from '@mui/material';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  IDocument,
  NoteDTO,
  createInvoiceNote,
  createNote,
  createPlacementNote,
  updateInvoiceNote,
  updateNote,
  updatePlacementNote,
  uploadFileToS3,
} from 'wisestep-api';
import {
  ControlledTextInput,
  WsButton,
  WsColors,
  WsFileUpload,
  WsSelect,
  WsTypoVariants,
  WsTypography,
} from 'wisestep-base';

import { NotesParent } from '../../../../components/communicationBar/Notes';
import WsTagsFilter from '../../../../components/filters/components/WsTagsFilter';
import { downloadCV } from '../../../../features/conversationWidget/utils/cvHelper';
import { failed, success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';

interface INotes {
  parentId: string;
  parent: NotesParent;
  isClient?: boolean;
  clientContactId?: string;
  clientContactName?: string;
  email?: string;
  onCancel: () => void;
  clientName?: string;
}

const dropdownOptions = [
  { label: 'Phone', value: 'Phone' },
  { label: 'Email', value: 'Email' },
  { label: 'SMS', value: 'SMS' },
  { label: 'Other', value: 'Other' },
];

const NotesEditingForm = ({
  parentId,
  parent,
  isClient,
  clientContactId,
  onCancel,
  clientName,
  clientContactName,
}: INotes) => {
  const methods = useForm();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [otherDocument, setOtherDocument] = useState<IDocument[]>([]);

  const saveNote = async (data) => {
    try {
      const noteData = {
        ...data,
        client: { id: parentId },
        markClientNotes: isClient,
        ...(parent === NotesParent.CLIENTCONTACT && { clientContact: { id: clientContactId } }),
        ...(parent === NotesParent.INVOICE && { invoiceId: parentId }),
        ...(parent === NotesParent.PLACEMENT && { closureId: parentId }),
      };
      const notePromise = data.id
        ? (parent === NotesParent.INVOICE
            ? updateInvoiceNote
            : parent === NotesParent.PLACEMENT
            ? updatePlacementNote
            : updateNote)(data)
        : (parent === NotesParent.INVOICE
            ? createInvoiceNote
            : parent === NotesParent.PLACEMENT
            ? createPlacementNote
            : createNote)(noteData as NoteDTO);
      await notePromise;
      setNotification(success(`Note has been created successfully !!`));
      methods.reset();
    } catch (e) {
      setNotification(failed('Something went wrong !!'));
    }
    onCancel();
  };

  const onUpload = async (file: File[], field: string) => {
    const url = await uploadFileToS3(file[0]);
    if (field === 'document') {
      const alteredDocuments = [...otherDocument];
      alteredDocuments.push({
        documentLocation: url,
        originalName: file[0].name,
        tag: null,
        uploadedOn: new Date(),
      });
      setOtherDocument([...alteredDocuments]);
    }
  };

  const removeDocHanlder = () => setOtherDocument([]);

  const handleFormSubmit = (data) => {
    data.fileUrl = otherDocument?.[0]?.documentLocation?.split('?')?.[0] ?? '';
    data.fileName = otherDocument?.[0]?.originalName;
    saveNote(data);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <DialogTitle>
          {parent === NotesParent.CLIENTCONTACT ? (
            <WsTypography variant={WsTypoVariants.TITLE}>
              Create Note for {clientContactName}
            </WsTypography>
          ) : (
            <WsTypography variant={WsTypoVariants.TITLE}>Create Note for {clientName}</WsTypography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1} mt={2} direction="column">
            <Grid item xs={8} md={8}>
              <ControlledTextInput name="heading" fullWidth required placeholder="Heading" />
            </Grid>
            <Grid item xs={4} md={4}>
              <WsSelect
                name="noteType"
                selectArray={dropdownOptions}
                required
                showMandatorySymbol
                defaultValue=""
                size="small"
                placeholder="Add Notes Type"
              />
            </Grid>
            <Grid item>
              <ControlledTextInput
                name="description"
                fullWidth
                multiline
                placeholder="Description"
                inputProps={{}}
                minRows={3}
              />
            </Grid>
            <Grid item p={1} sm={5.75}>
              <WsTagsFilter placeHolder="Enter Tags" name="tags" label="" />
            </Grid>
            <Grid item>
              {otherDocument?.length > 0 ? (
                otherDocument?.map((document, index: number) => (
                  <Stack key={index} direction="row" spacing={2} mb={2}>
                    <WsTypography variant={WsTypoVariants.BODY1}>
                      {document?.originalName ?? ''}
                    </WsTypography>
                    <DownloadIcon
                      onClick={() =>
                        downloadCV(
                          document?.documentLocation?.split('?')[0] ?? '',
                          document?.originalName ?? ''
                        )
                      }
                      sx={{
                        color: WsColors.TheBlues500,
                        fontSize: '24px',
                        cursor: 'pointer',
                      }}
                    />
                    <CloseIcon
                      onClick={removeDocHanlder}
                      sx={{ color: WsColors.Red500, cursor: 'pointer' }}
                    />
                  </Stack>
                ))
              ) : (
                <WsFileUpload
                  multiple={false}
                  onUpload={(file: File[]) => onUpload && onUpload(file, 'document')}
                  name={'document'}
                  placeholderText=""
                  bgRequired={false}
                  buttonText={'Upload Other Files'}
                  maxFileSize={3}
                />
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Stack direction="row" mt={1} spacing={2} justifyContent="flex-end">
            <WsButton variant="text" size="large" onClick={onCancel}>
              Cancel
            </WsButton>
            <WsButton size="large" type="submit">
              Save
            </WsButton>
          </Stack>
        </DialogActions>
      </form>
    </FormProvider>
  );
};
export default NotesEditingForm;
